import { CANONICAL_BASE_URL, getRouteSeoMeta } from '../config/routeSeo';
import {
  DEFAULT_MONTHLY_DISPLAY_RATE,
  getLocationMonthlyDisplayMeta,
  getLocationRates,
  MONTHLY_DISPLAY_MODES,
} from './pricing';
import { buildCanonicalPricingPath, parsePricingPath } from './pricingPath';
import { buildLocationSeoProfile, buildSchemaAmenityFeatures } from './locationSeoProfile';

const DEFAULT_BUSINESS_NAME = 'Truck Parking Hub';
const DEFAULT_BUSINESS_EMAIL = 'tph@trucksparkinghub.com';
const SEO_MONTHLY_RATE_FLOOR = DEFAULT_MONTHLY_DISPLAY_RATE;
const PRICING_ROUTE_META_OVERRIDES = {
  '/pricing/nc/charlotte/raleigh-street-location3826': {
    description: `Truck parking in Charlotte, NC at 3826 Raleigh Street. Daily $27 | Weekly $180 | Monthly from $${SEO_MONTHLY_RATE_FLOOR}. 24/7 access, paved lot, perimeter fencing, cameras. Book today.`,
  },
};

const normalizePathname = (pathname = '/') => {
  if (!pathname) return '/';
  const raw = String(pathname || '').trim();
  if (!raw) return '/';
  let normalizedInput = raw;

  if (/^https?:\/\//i.test(raw)) {
    try {
      normalizedInput = new URL(raw).pathname || '/';
    } catch (_error) {
      normalizedInput = raw;
    }
  }

  const withoutQuery = normalizedInput.split('?')[0]?.split('#')[0] || '/';
  if (withoutQuery === '/') return '/';
  return withoutQuery.replace(/\/+$/, '') || '/';
};

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseAddressParts = (address = '') => {
  const parts = String(address || '')
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean);

  const streetAddress = parts[0] || '';
  const addressLocality = parts[1] || '';
  const stateZip = parts[2] || '';
  const [addressRegion = '', postalCode = ''] = stateZip.split(/\s+/).filter(Boolean);

  return {
    streetAddress,
    addressLocality,
    addressRegion,
    postalCode,
  };
};

const normalizeUsPhoneForSchema = (phone = '') => {
  const raw = String(phone || '').trim();
  if (!raw) return null;

  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;

  if (digits.length === 10) {
    return `+1-${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    const local = digits.slice(1);
    return `+1-${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
  }

  return raw.startsWith('+') ? raw : `+${digits}`;
};

const getMonthlyRate = (location = null) => {
  return getLocationMonthlyDisplayMeta(location).amount;
};

const buildMonthlyPricingDescriptionSegment = (location = null) => {
  const monthlyDisplay = getLocationMonthlyDisplayMeta(location);
  return monthlyDisplay.mode === MONTHLY_DISPLAY_MODES.AS_LOW_AS
    ? `Monthly from $${monthlyDisplay.amount}`
    : `Monthly $${monthlyDisplay.amount}`;
};

const buildOfferSchema = (price, serviceName, canonicalUrl) => ({
  '@type': 'Offer',
  priceCurrency: 'USD',
  price: String(price),
  availability: 'https://schema.org/InStock',
  url: canonicalUrl,
  itemOffered: {
    '@type': 'Service',
    name: serviceName,
  },
});

const decodeSlugToken = (value = '') => (
  String(value || '')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const normalizeSeoTitleSegment = (value = '') => String(value || '').trim();

const buildPricingPageTitle = ({
  city = 'this area',
  stateCode = 'US',
  areaLabel = 'Main Access Corridor',
} = {}) => {
  const normalizedCity = normalizeSeoTitleSegment(city) || 'this area';
  const normalizedStateCode = (normalizeSeoTitleSegment(stateCode) || 'US').toUpperCase();
  const normalizedAreaLabel = normalizeSeoTitleSegment(areaLabel) || 'Main Access Corridor';
  return `Truck Parking in ${normalizedCity}, ${normalizedStateCode} | ${normalizedAreaLabel} | Truck Parking Hub`;
};

const resolvePricingRouteMetaOverride = ({ pathname = '', location = null } = {}) => {
  const pathCandidates = [
    normalizePathname(pathname),
    normalizePathname(buildCanonicalPricingPath(location) || ''),
  ].filter(Boolean);

  for (const candidate of pathCandidates) {
    if (PRICING_ROUTE_META_OVERRIDES[candidate]) {
      return PRICING_ROUTE_META_OVERRIDES[candidate];
    }
  }

  return null;
};

const buildPricingPageDescription = ({
  pathname = '',
  location = null,
  city = 'this area',
  stateCode = 'US',
  highwayOrCorridor = 'major freight corridors',
  topAmenities = 'Security-forward access and driver-focused lot operations',
  daily = 27,
  weekly = 180,
  monthly = SEO_MONTHLY_RATE_FLOOR,
} = {}) => {
  const override = resolvePricingRouteMetaOverride({ pathname, location });
  if (override?.description) return override.description;

  const monthlyDescription = location
    ? buildMonthlyPricingDescriptionSegment(location)
    : `Monthly from $${monthly}`;

  return `Secure truck parking in ${city}, ${stateCode} near ${highwayOrCorridor}. Daily $${daily} · Weekly $${weekly} · ${monthlyDescription}. ${topAmenities}. Reserve online 24/7.`;
};

const resolveLocationSameAsLinks = (location = null) => {
  const links = Array.isArray(location?.sameAsLinks) ? location.sameAsLinks : [];
  const normalized = [...new Set(
    links
      .map((entry) => String(entry || '').trim())
      .filter(Boolean)
  )];

  if (normalized.length > 0) return normalized;

  const reviewLink = typeof location?.reviewLink === 'string' ? location.reviewLink.trim() : '';
  return reviewLink ? [reviewLink] : undefined;
};

const buildPricingSeoContext = ({ selectedLocation, selectedAddress, pathname }) => {
  if (selectedLocation) {
    const profile = buildLocationSeoProfile(selectedLocation);
    const { daily, weekly } = getLocationRates(selectedLocation);
    const monthly = getMonthlyRate(selectedLocation);
    return {
      city: profile.city,
      stateCode: profile.stateCode,
      areaLabel: profile.areaLabel,
      highwayOrCorridor: profile.highwayOrCorridor,
      topAmenities: profile.topAmenities,
      daily,
      weekly,
      monthly,
      h1: `Truck Parking in ${profile.city}, ${profile.stateCode} \u2013 ${profile.areaLabel}`,
    };
  }

  const selectedAddressParts = parseAddressParts(selectedAddress || '');
  const parsedPricingPath = parsePricingPath(pathname);
  const fallbackCity = selectedAddressParts.addressLocality || decodeSlugToken(parsedPricingPath.citySlug) || 'this area';
  const fallbackState = String(selectedAddressParts.addressRegion || parsedPricingPath.stateCode || 'US').toUpperCase();
  const fallbackArea = selectedAddressParts.streetAddress
    || decodeSlugToken(parsedPricingPath.streetLocationToken?.replace(/-location[\w-]*$/i, ''))
    || 'Main Access Corridor';

  return {
    city: fallbackCity,
    stateCode: fallbackState,
    areaLabel: fallbackArea,
    highwayOrCorridor: 'major freight corridors',
    topAmenities: 'Security-forward access and driver-focused lot operations',
    daily: 27,
    weekly: 180,
    monthly: SEO_MONTHLY_RATE_FLOOR,
    h1: `Truck Parking in ${fallbackCity}, ${fallbackState} \u2013 ${fallbackArea}`,
  };
};

export const buildLocationPricingSchemas = ({
  location = null,
  canonicalUrl = '',
} = {}) => {
  if (!location || !canonicalUrl) return [];

  const profile = buildLocationSeoProfile(location);
  const pageTitle = buildPricingPageTitle({
    city: profile.city,
    stateCode: profile.stateCode,
    areaLabel: profile.areaLabel,
  });
  const locationName = location.title || location.address || DEFAULT_BUSINESS_NAME;
  const { daily, weekly } = getLocationRates(location);
  const monthly = getMonthlyRate(location);
  const pageDescription = buildPricingPageDescription({
    pathname: canonicalUrl,
    location,
    city: profile.city,
    stateCode: profile.stateCode,
    highwayOrCorridor: profile.highwayOrCorridor,
    topAmenities: profile.topAmenities,
    daily,
    weekly,
    monthly,
  });
  const { streetAddress, addressLocality, addressRegion, postalCode } = parseAddressParts(location.address);
  const latitude = toFiniteNumber(location?.coordinates?.lat);
  const longitude = toFiniteNumber(location?.coordinates?.lng);
  const hasAddress = streetAddress && addressLocality && addressRegion;
  const hasCoordinates = latitude !== null && longitude !== null;
  const sameAsLinks = resolveLocationSameAsLinks(location);

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'ParkingFacility'],
    '@id': `${canonicalUrl}#location`,
    name: locationName,
    url: canonicalUrl,
    image: `${CANONICAL_BASE_URL}/tph-logo-desktop.png`,
    telephone: normalizeUsPhoneForSchema(location?.phone || profile.supportPhone) || undefined,
    email: (!location?.phone && !profile.supportPhone) ? DEFAULT_BUSINESS_EMAIL : undefined,
    priceRange: `$${daily}-$${monthly}`,
    areaServed: [profile.city, profile.stateName].filter(Boolean).join(', ') || undefined,
    hasMap: profile.mapUrl || undefined,
    openingHours: profile.openingHours.length > 0 ? profile.openingHours : undefined,
    amenityFeature: buildSchemaAmenityFeatures(profile),
    address: hasAddress
      ? {
          '@type': 'PostalAddress',
          streetAddress,
          addressLocality,
          addressRegion,
          postalCode: postalCode || undefined,
          addressCountry: 'US',
        }
      : undefined,
    geo: hasCoordinates
      ? {
          '@type': 'GeoCoordinates',
          latitude,
          longitude,
        }
      : undefined,
    sameAs: sameAsLinks,
    offers: [
      buildOfferSchema(daily, 'Daily Truck Parking', canonicalUrl),
      buildOfferSchema(weekly, 'Weekly Truck Parking', canonicalUrl),
      buildOfferSchema(monthly, 'Monthly Truck Parking', canonicalUrl),
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: profile.faq.slice(0, 6).map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: CANONICAL_BASE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Truck Parking',
        item: `${CANONICAL_BASE_URL}/truck-parking`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: locationName,
        item: canonicalUrl,
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    url: canonicalUrl,
    description: pageDescription,
  };

  return [localBusinessSchema, faqSchema, breadcrumbSchema, webPageSchema];
};

/**
 * Generates props for the SEO component based on the active route.
 *
 * @param {Object} options
 * @param {string} [options.pathname='/']
 * @param {Object} [options.routeSeoMeta]
 * @param {string|null} options.preselectedLocationId
 * @param {string|null} options.selectedAddress
 * @param {Object} options.currentSEO
 * @param {string} options.keywords
 * @param {string} [options.baseUrl]
 * @returns {Object}
 */
export const generateSEOProps = ({
  pathname = '/',
  routeSeoMeta = null,
  preselectedLocationId,
  selectedAddress,
  selectedLocation = null,
  currentSEO,
  keywords,
  baseUrl = CANONICAL_BASE_URL,
}) => {
  const resolvedRouteSeo = routeSeoMeta || getRouteSeoMeta(pathname);
  const canonicalPricingPath = buildCanonicalPricingPath(selectedLocation);
  const resolvedCanonicalPath = canonicalPricingPath
    || resolvedRouteSeo?.canonicalPath
    || (preselectedLocationId ? `/pricing/${preselectedLocationId}` : '/');

  const currentPricingPath = parsePricingPath(pathname);
  const canonicalPricingRoutePath = parsePricingPath(resolvedCanonicalPath);
  const isPricingRoute = resolvedRouteSeo?.routeKey === 'pricing'
    || currentPricingPath.isPricingRoute
    || canonicalPricingRoutePath.isPricingRoute;
  const hasSelectedPricingLocation = Boolean(isPricingRoute && (preselectedLocationId || selectedLocation));

  let title = resolvedRouteSeo?.title || currentSEO?.title;
  let description = resolvedRouteSeo?.description || currentSEO?.description;
  let h1 = null;

  if (hasSelectedPricingLocation) {
    const pricingContext = buildPricingSeoContext({
      selectedLocation,
      selectedAddress,
      pathname,
    });

    const city = normalizeSeoTitleSegment(pricingContext.city);
    const stateCode = normalizeSeoTitleSegment(pricingContext.stateCode).toUpperCase();
    const areaLabel = normalizeSeoTitleSegment(pricingContext.areaLabel);
    const highwayOrCorridor = normalizeSeoTitleSegment(pricingContext.highwayOrCorridor) || 'major freight corridors';
    const topAmenities = normalizeSeoTitleSegment(pricingContext.topAmenities) || 'Security-forward access and driver-focused lot operations';
    const { daily, weekly, monthly } = pricingContext;

    title = buildPricingPageTitle({ city, stateCode, areaLabel });
    description = buildPricingPageDescription({
      pathname: canonicalPricingPath || resolvedCanonicalPath || pathname,
      location: selectedLocation,
      city,
      stateCode,
      highwayOrCorridor,
      topAmenities,
      daily,
      weekly,
      monthly,
    });
    h1 = pricingContext.h1;
  }

  return {
    title,
    description,
    h1,
    keywords,
    url: `${baseUrl}${resolvedCanonicalPath}`,
    image: resolvedRouteSeo?.ogImage || `${baseUrl}/tph-logo-desktop.png`,
    section: currentSEO?.section || 'Home',
    robots: resolvedRouteSeo?.robots || 'index,follow',
    schemaType: resolvedRouteSeo?.schemaType,
  };
};

export default {
  generateSEOProps,
  buildLocationPricingSchemas,
};
